<template>
  <div class="person-card" :selected="selectedRef" :class="cardClassRef">
    <button type="button" v-if="useCardButtonRef && selectModeRef" class="overlay" :disabled="disabled" @click="onClick"></button>
    <router-link v-if="useCardButtonRef && linkModeRef"  class="overlay" :disabled="disabled" :to="linkUrlRef"/>
    <div class="gender-bar" :class="genderClassRef"></div>
    <button type="button" class="btn btn-unstyled person-card-image" :class="{ 'placeholder': !personImageUrlRef }" :disabled="!isInteractiveRef" @click="onClickImage">
      <img 
        :src="personImageSrcRef"
        :srcset="personImageSrcsetRef"
        :sizes="personImageSizesRef"
      />
    </button>
    <div class="person-card-body">
      <div class="title-area">
        <button type="button" v-if="!useCardButtonRef && selectModeRef" class="overlay" :disabled="disabled" @click="onClick"></button>
        <router-link v-if="!useCardButtonRef && linkModeRef"  class="overlay" :disabled="disabled" :to="linkUrlRef"/>
        <div class="title">
          {{ cardTitleRef }}
        </div>
        <div v-if="showProfileTypeRef" class="subtitle">
          <LinkIcon v-if="profileTypeRef == ProfileType.ManagedLinked" size="1x"/>
          <LockIcon v-if="personRef?.restricted" size="1x"/>
          {{ profileTypeTextRef }}
          <UserLink v-if="ownerRef" :userId="ownerRef?.id" dimmed/>
        </div>
      </div>
      <div v-if="showLifespanRef" class="lifespan">{{ displayPropsRef?.lifespanText }}</div>
      <div v-if="showProfileCountRef" class="profiles">
        <button type="button" v-if="profileCountRef && !props.static" class="btn btn-link btn-inline link-secondary" @click="onShowProfiles">
          {{ profileCountRef }} {{ profilesTextRef }}
        </button>
        <span v-else-if="profileCountRef">
          {{ profileCountRef }} {{ profilesTextRef }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$transition-duration: 0.3s;
$transition-timing: ease-in-out;
$transition-late-delay: 0.2s;
$transition-late-duration: 0.1s;

.person-card {
  position: relative; // for whole-card overlay button
  display: flex;
  width: 100%;
  border-top: 1px solid #f8f8f8;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
  transition-property: width, height;
  transition-duration: $transition-duration;
  transition-timing-function: $transition-timing;

  &.show-hover:hover {
    box-shadow: 1px 3px 6px rgba(0, 0, 0, 0.15);
  }

  .gender-bar {
    position: relative;
    flex: 0 0 8px;
    height: 100%;
    background-color: #e9e9e9;
    transition: flex-basis $transition-duration $transition-timing;

    &.male {
      background-color: #c3d5ea;
    }

    &.female {
      background-color: #fadce1;
    }
  }

  .person-card-image {
    position: relative;
    flex: 0 0 60px;
    transition: flex-basis $transition-duration $transition-timing;

    &.placeholder {
      padding: 6px;
      display: flex; // only to center placeholder image
      align-items: center;
      justify-content: center;
      background-color: #f4f4f4;
      
      img {
        opacity: 20%;
      }
    }

    @media (min-width: 768px) {
      flex: 0 0 100px;

      &.placeholder {
        padding: 15px;
      }
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .person-card-body {
    align-self: center;
    height: 100%;
    width: 100%;
    min-width: 0; // for some reason this is needed to enable overflow control in .person-name
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 0.5em;
    line-height: 1.3; // tight line spacing but don't clip glyph descenders (1.2 is too tight)
    overflow: hidden;
    transition: padding $transition-duration $transition-timing;
    transition-property: padding, height;
    
    .title-area {
      position: relative; // for title-only overlay button
      display: flex;
      flex-direction: column;
    }

    .subtitle {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 0.75rem;
      font-weight: 400;
      color: #888;
    }
    
    .lifespan, .profiles {
      max-height: 0;
      opacity: 0;
      font-size: 0.75rem;
      font-weight: 400;
      color: #888;
      transition: opacity $transition-late-duration $transition-timing;
    }
  }

  .card-button {
    display: block;    
    width: 100%; // force it to size to parent instead of content

    &:hover {
      color: inherit;
    }
  }

  .title {
    display: block; // to get max clickable area
    width: 100%;
    min-height: 0;
    padding: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: font-size $transition-duration $transition-timing;
    transition-property: font-size, padding;
  }

  .birth-year {
    margin: 0;
    margin-left: 0.25em;
    font-size: 0.875em; // relative size
    color: #aaa;
  }

  &.empty, &.dangling {
    .person-card-image {
      background-color: inherit;

        &.placeholder img {
          opacity: 60%;
        }
    }

    .title {
      color: #aaa;
    }
  }

  &.card-full {
    height: 75px;

    @media (min-width: 768px) {
      height: 100px;
    }

    .person-card-body {
      padding: 0.1em 0.5em 0.3em;
    }

    .title {
      padding: 0.25rem 0; // ensure descenders are not clipped
      display: -webkit-box; // to get max clickable area
      white-space: normal;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      pointer-events: none;
      cursor: default;
    }

    @media (min-width: 768px) {
      font-size: 1.25rem;
      font-weight: 300;
    }

    .birth-year {
      display: none;
    }

    .lifespan {
      margin-top: auto;
    }

    .lifespan, .profiles {
      max-height: none;
      opacity: 1;
      transition-delay: $transition-late-delay; // show late when this is the final state
    }
  }

  &.card-small {
    height: 40px;
    max-width: 400px;

    .title {
      font-size: 0.875rem;
    }

    .person-card-image {
      flex: 0 0 36px;

      &.placeholder {
        padding: 4px;
      }
    }

    .lifespan {
      margin-top: 0;
    }

    .lifespan, .profiles {
      max-height: none;
      opacity: 1;
      transition-delay: $transition-late-delay; // show late when this is the final state
    }
  }

  &.card-tiny {
    //width: 80px;
    height: 26px; // ~1.6rem

    .gender-bar {
      flex: 0 0 6px;
    }

    .title {
      font-size: 0.75rem;
    }

    .person-card-image {
      flex: 0 0 0;
      padding: 0;
    }
  }

  &.card-dot {
    width: 12px;
    height: 8px;
    border-top: none;
    box-shadow: none;
    font-size: 0.75rem;

    .gender-bar {
      flex: 0 0 12px;
    }

    .person-card-image {
      flex: 0 0 0;
      padding: 0;
    }

    .title {
      text-overflow: clip; // for animation performance
    }
  }

  &.restricted {
    .person-card-image {
      background-color: transparent;

      img {
        opacity: 80%;
      }
    }
  }

  &.disabled {
    box-shadow: none;
    border: 1px solid #f4f4f4;

    // apply opacity to child elements so parent opacity can be animated for fade in/out transitions
    .gender-bar, .person-card-image {
      opacity: 0;
    }
    
    .person-card-body {
      .title {
        color: #aaa;
      }
      .subtitle, .lifespan {
        color: #bbb;
      }
    }
    
    button.btn.card-button:disabled {
      // override btn.disabled styles, since we want to dim the card as a whole
      color: inherit;
      opacity: 1;
    }
  }

  &.raised {
    box-shadow: 0 0.125rem 0.35rem rgba(0,0,0,0.25); // stronger shadow
  }

  &.highlight:not(.disabled) {
    background-color: #fefcd5;

    .gender-bar::before,
    .person-card-image::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(yellow, 0.1)
    }

    .card-button {
      cursor: default;

      &:hover {
        color: rgb(33, 37, 41);
      }
    }
  }
  
  &.dimmed:not(.disabled) {
    .gender-bar {
      opacity: 0.4;
    }

    .person-card-image {
      opacity: 0.4;
    }

    .card-button {
      cursor: default;
    }

    .title {
      color: #aaa !important;

      .birth-year small {
        color: #ddd !important;
      }
    }

    .select-checkbox {
      background-color: white;
    }
  }  
}
</style>

<script setup lang="ts">
import { computed, PropType } from 'vue'
import { ContentDataAltKey, Gender, getContentDataAltSize, genderImages, dottedPersonImage, Person, ViewPerson } from '@/rd/ResearchDataModel'
import { usePersonStore } from '@/rd/PersonStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useContentStore } from '@/rd/ContentStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useUserStore } from '@/gp/UserStore'
import { useExploreStore } from '@/explore/ExploreStore'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import { ProfileUtils, ProfileType } from './profiles/ProfileUtils'
import { LinkIcon, LockIcon } from '@zhuowenli/vue-feather-icons'
import UserLink from '@/manage/users/UserLink.vue'

const props = defineProps({
  personId: String,
  layout: String as PropType<"full" | "small" | "tiny" | "dot">,
  disabled: Boolean,
  selectMode: Boolean,
  selectEmpty: Boolean,
  static: Boolean,
  selectedId: String,
  highlightMode: Boolean,
  highlighted: Boolean,
  dimmed: Boolean,
  raised: Boolean,
  showProfileType: Boolean,
  showProfileCount: String as PropType<'always' | 'multiple'>,
  showRestricted: Boolean,
  noHover: Boolean,
})

const emit = defineEmits([
  'click',
  'clickImage',
  'showProfiles',
  'update:selectedId',
])

const viewPersonStore = useViewPersonStore()
const personStore = usePersonStore()
const contentStore = useContentStore()
const dataGroupStore = useDataGroupStore()
const userStore = useUserStore()
const exploreStore = useExploreStore()

const layoutRef = computed(() => props.layout ?? 'full')
const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded)?.data)
const personRef = computed(() => isSingletonRef.value ? personStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded)?.data : undefined)
const displayPropsRef = computed(() => viewPersonRef.value?.displayProperties)
const cardTitleRef = computed(() => 
  !props.personId ? '(none)' :
  isDanglingRef.value ? '(deleted or not visible)' : 
  displayPropsRef.value?.displayName ?? "(no name)"
)

const dataGroupRef = computed(() => isSingletonRef.value ? dataGroupStore.getAsyncGroup(CompositeId.getGroupId(props.personId), LoadMode.EnsureLoaded)?.data : undefined)
const ownerRef = computed(() => userStore.getAsyncUser(dataGroupRef.value?.ownerId, LoadMode.EnsureLoaded)?.data)
const profileTypeRef = computed(() => ProfileUtils.getProfileType(personRef.value, dataGroupRef.value, ownerRef.value))
const profileTypeTextRef = computed(() => ProfileUtils.getProfileSubtitle(profileTypeRef.value))
const showProfileTypeRef = computed(() => isSingletonRef.value && props.showProfileType)

const cardClassRef = computed(() => ({ 
  'disabled': props.disabled,
  'highlight': props.highlightMode && props.highlighted,
  'dimmed': props.dimmed || (props.highlightMode && !props.highlighted),
  'raised': props.raised,
  ['card-' + layoutRef.value]: true,
  'restricted': personRef.value?.restricted,
  'empty': !props.personId,
  'dangling': isDanglingRef.value,
  'show-hover': isInteractiveRef.value && !props.noHover && !props.disabled,
}))
const genderClassRef = computed(() => [displayPropsRef.value?.gender?.toLowerCase()])
const personImageUrlRef = computed(() => displayPropsRef.value?.primaryPhotoSignedUrl?.signedUrl)
const placeholderImageUrlRef = computed(() => 
  personRef.value?.restricted ? dottedPersonImage :
  profileCountRef.value == 0 ? dottedPersonImage :
  genderImages[displayPropsRef.value?.gender ?? "default"])
const personImageSrcRef = computed(() => personImageUrlRef.value ?? placeholderImageUrlRef.value)
const personImageContentRef = computed(() => {
  if (props.layout == 'full') {
    const contentId = displayPropsRef.value?.primaryPhotoSignedUrl?.contentId
    return contentStore.getContents([contentId], LoadMode.EnsureLoaded).at(0)
  }
  return undefined
})
const personImageAltKeys = [ContentDataAltKey.Gallery, ContentDataAltKey.Thumb]
const personImageSrcsetRef = computed(() => {
  const signedUrls = personImageContentRef.value?.dataSignedUrls
  if (signedUrls) {
    const orig = signedUrls.find(u => !u.altKey)
    const gal = signedUrls.find(u => u.altKey == ContentDataAltKey.Gallery)
    const thu = signedUrls.find(u => u.altKey == ContentDataAltKey.Thumb)
    const sources: string[] = []
    if (thu) {
      sources.push(`${thu.signedUrl} ${getContentDataAltSize(ContentDataAltKey.Thumb)}w`)
    }
    if (gal || orig) {
      const useGal = (gal ?? orig)!
      sources.push(`${useGal.signedUrl} ${getContentDataAltSize(ContentDataAltKey.Gallery)}w`)
    }
    return sources.join(', ')
  }
  return undefined
})
const personImageSizesRef = computed(() => {
  switch (layoutRef.value) {
    case 'full': return "(min-width: 768px) 100px, 60px" // from CSS above
    case 'small': return "36px"
    default: return undefined
  }
})

const showLifespanRef = computed(() => layoutRef.value == 'full' || !(showProfileCountRef.value || showProfileTypeRef.value))
const profileIdsRef = computed(() => {
  if (!props.personId)
    return []

  const matchIds = viewPersonRef.value?.matchIds ?? []
  const matches = personStore.getLoadedPersonList(matchIds)
  return matches.filter(p => (p.profile && !p.placeholder) || (!p.profile && (props.showRestricted || !p.restricted)))
    .map(p => p.id!)
})
const profileCountRef = computed(() => profileIdsRef.value.length ?? 0)
const showProfileCountRef = computed(() => 
  props.showProfileCount == 'always' || 
  (props.showProfileCount == 'multiple' && profileCountRef.value > 1))
const isDanglingRef = computed(() => props.personId && profileCountRef.value == 0)
const isSingletonRef = computed(() => profileCountRef.value == 1)
const isEmptyRef = computed(() => profileCountRef.value == 0)
const profilesTextRef = computed(() => profileCountRef.value == 1 ? "profile" : "profiles")

const selectModeRef = computed(() => !props.static && props.selectMode && (props.selectEmpty || !isEmptyRef.value) && !props.disabled)
const linkModeRef = computed(() => !props.static && !props.selectMode && !isDanglingRef.value && !isEmptyRef.value && !props.disabled)
const isInteractiveRef = computed(() => selectModeRef.value || linkModeRef.value)
const linkUrlRef = computed(() => ViewPerson.getPageUrl(props.personId ?? ''))
const useCardButtonRef = computed(() => props.layout != 'full')

const selectedRef = computed({
  get: () => props.selectMode && props.selectedId == props.personId,
  set: (value) => {
    emit('update:selectedId', value ? props.personId : undefined) // toggle selection of this item
  }
})

function onClick() {
  // do this even if no personId specified
  emit('click', props.personId) // for stateless selection
  selectedRef.value = false // for stateful selection (click deselects, state can only be set by parent)
}

function onClickImage() {
  const contentId = personRef.value?.displayProperties.primaryPhotoSignedUrl?.contentId
  if (contentId) {
    emit('clickImage', contentId)
  }
}

function onShowProfiles() {
  emit('showProfiles')
}

function setStartPerson() {
  if (props.personId) {
    exploreStore.setStartPersonId(props.personId)
  }
}
</script>
